import { defineStore } from 'pinia'
import {
  buildAccountExperienceOwnerKey,
  resetAccountExperienceCache,
} from '../utils/accountExperienceCache.js'

export const useUserStore = defineStore('user', {
  state: () => ({
    profile: null,
    httpToken: null, // Will store { token: 'xyz', type: 'bearer' }
    selectedBusinessId: null, // Track selected business for multi-business staff
  }),
  getters: {
    isAuthenticated: state => !!state.httpToken && !!state.profile,
    isCustomer: state => !!state.httpToken && !!state.profile && state.profile.role === 'Customer',
    isCompany: state => !!state.httpToken && !!state.profile && state.profile.role === 'Company',
    userInitials: state => {
      if (state.profile && state.profile.firstName) {
        return (state.profile.firstName[0] || '').toUpperCase()
      }

      return 'G'
    },
    getWsToken: state => state.profile?.wsToken,
  },
  actions: {
    async login(userProfile, accessTokenPayload) { // accessTokenPayload = { token: '...', type: '...' }
      this.profile = userProfile
      this.httpToken = accessTokenPayload // Store the whole token object
      resetAccountExperienceCache(buildAccountExperienceOwnerKey(userProfile))

      // wsToken is expected to be part of userProfile
    },
    /**
     * Pure state-clear. Does NOT navigate. Callers decide where to send the
     * user (e.g. /login with a redirectTo query for a 401 retry, or /login
     * plain for an explicit logout from the user menu).
     */
    clearSession() {
      console.log("Clearing user session state.")
      resetAccountExperienceCache()

      if (typeof window !== 'undefined') {
        // Give authenticated realtime clients a chance to unsubscribe,
        // deauthenticate, and remove their persisted SocketCluster token.
        window.dispatchEvent(new CustomEvent('app:logout'))
      }

      // Tear down dependent state so the next user's session doesn't inherit
      // stale cache or business context from the previous session.
      try {
        const cacheStore = useCacheStore()
        cacheStore.$reset()
      } catch (e) {
        console.warn('clearSession: failed to reset cache store', e)
      }
      try {
        const { clearContext } = useBusinessContext()
        clearContext()
      } catch (e) {
        console.warn('clearSession: failed to clear business context', e)
      }
      try {
        // Tear down Capacitor push-notification listeners so the next user
        // (or anonymous session) doesn't receive notifications routed to
        // the previous user's listener handlers. Note: the device-token row
        // backend-side stays active until the device explicitly de-registers
        // (separate follow-up — needs a deactivate-by-token endpoint).
        const { cleanup } = usePushNotifications()
        cleanup?.()
      } catch (e) {
        console.warn('clearSession: failed to clean up push listeners', e)
      }

      this.profile = null
      this.httpToken = null
      this.selectedBusinessId = null

      if (typeof localStorage !== 'undefined') {
        // pinia-plugin-persistedstate writes one key per store; remove the ones
        // we know about so the next mount starts from a clean slate.
        localStorage.removeItem('user')
        localStorage.removeItem('cache')
      }
    },
    /**
     * Backwards-compatible wrapper: clears session and navigates to /login.
     * Most UI callers (user menu, profile page) want exactly this.
     * Avoid using from auth-failure handlers — call clearSession() and let
     * the handler navigate with its own redirectTo query.
     */
    logout() {
      this.clearSession()
      navigateTo('/login', { replace: true })
    },
    setProfile(userProfile) { // If fetching profile separately later
      this.profile = userProfile
      resetAccountExperienceCache(buildAccountExperienceOwnerKey(userProfile))
    },
    setHttpToken(tokenPayload) { // If setting token from persisted state on init
      this.httpToken = tokenPayload
    },
    setSelectedBusiness(businessId) {
      this.selectedBusinessId = businessId
    },
  },
  persist: true,
})
